import { defaultLayerSettings, layerDefaultSettings, layerSettingsAtom, layerSettingsManyAtom, tLayerSettings } from '@/atoms';
import { execute } from '@/graphql/execute';
import { layerSettingsQuery } from '@/queries/layer.query';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';
import { useMemo } from 'react';

export const useLayerSettings = (layerId: string) => {
  const [settings, setSettings] = useAtom(layerSettingsAtom(layerId));
  const layerQuery = useQuery({
    queryKey: ['layer-settings', layerId],
    queryFn: () => execute(layerSettingsQuery, { id: layerId }),
  });

  return {
    settings: settings || layerQuery.data?.layer?.settings || layerDefaultSettings(layerQuery.data?.layer),
    setSettings,
    layer: layerQuery.data?.layer,
  };
};

export const useLayerSettingsMany = (layerIds: string[]) => {
  const getLayerSettings = useAtomValue(layerSettingsManyAtom);
  const layerQueries = useQueries({
    queries: layerIds.map((layer) => ({
      queryKey: ['layer-settings', layer],
      queryFn: () => execute(layerSettingsQuery, { id: layer }),
    })),
  });
  const localSettings = getLayerSettings(layerIds);

  // Stringify dependencies for stable comparison
  const localSettingsKey = JSON.stringify(localSettings);
  const remoteSettingsKey = JSON.stringify(layerQueries.map((q) => q.data?.layer?.settings ?? null));

  const layerSettings = useMemo(
    () =>
      layerIds.reduce(
        (acc, layerId) => {
          const local = localSettings[layerId];
          const layerQuery = layerQueries.find((query) => query.data?.layer?.id === layerId);
          const remote = layerQuery?.data?.layer?.settings ?? null;
          acc[layerId] = local || remote || layerDefaultSettings(layerQuery?.data?.layer);
          return acc;
        },
        {} as Record<string, tLayerSettings>,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(layerIds), localSettingsKey, remoteSettingsKey],
  );

  return layerSettings;
};
